import axios, { AxiosResponse } from "axios";

import { getRequest } from "@/Utils/Service/BackendService";

import {
	Client,
	TableQueryFilter,
	TableQueryFilterOperands
} from "@/Contracts";
import {
	ClientsRequest,
	ClientsResponse,
	ClientContactsResponse,
	ClientContactRequest
} from "@/Contracts/Clients";
import {
	ChildEntityHydrationParams,
	getTableQueryFilters,
	includeChildEntities
} from "@/Contracts/Utils";

const getClientsUrl = "/v2/client/companies";
const getClientContactsUrl = "/v2/client/users";
const getClientDetailsUrl = "/v2/client/companies/{client_id}";

export const getClients = async (
	clientsRequest: ClientsRequest
): Promise<ClientsResponse> => {
	try {
		let queryParams = `${
			clientsRequest.limit && clientsRequest.limit > 0
				? `limit=${clientsRequest.limit}`
				: ""
		}`;

		if (clientsRequest.filters && clientsRequest.filters.length > 0) {
			const filterUrlQuery = getTableQueryFilters(
				clientsRequest.filters,
				false
			);
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				filterUrlQuery && filterUrlQuery.length > 0
					? `${filterUrlQuery}`
					: ""
			}`;
		}

		if (clientsRequest.keywords && clientsRequest.keywords.length > 0) {
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				clientsRequest.keywords && clientsRequest.keywords.length > 0
					? `q=${clientsRequest.keywords}`
					: ""
			}`;
		}

		if (
			clientsRequest.includeEntities &&
			clientsRequest.includeEntities.length > 0
		) {
			const childEntityUrlQuery = includeChildEntities(
				clientsRequest.includeEntities
			);
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				childEntityUrlQuery && childEntityUrlQuery.length > 0
					? `${childEntityUrlQuery}`
					: ""
			}`;
		}

		if (clientsRequest.sync_source) {
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				!!clientsRequest?.sync_source
					? `sync_source=${clientsRequest.sync_source}`
					: ""
			}`;
		}

		if (clientsRequest.selected_id) {
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				!!clientsRequest?.selected_id
					? `selected_id=${clientsRequest.selected_id}`
					: ""
			}`;
		}

		if (clientsRequest.page) {
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				!!clientsRequest?.page ? `page=${clientsRequest.page}` : ""
			}`;
		}

		const response: AxiosResponse<ClientsResponse> = await axios(
			getRequest(
				`${getClientsUrl}${
					queryParams.length > 0 ? `?${queryParams}` : ""
				}`,
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getClientContacts = async (
	clientContactRequest: ClientContactRequest
): Promise<ClientContactsResponse> => {
	try {
		let url = getClientContactsUrl;
		const appliedFilters = getTableQueryFilters(
			[
				...(!!clientContactRequest.filters.length
					? clientContactRequest.filters
					: []),
				new TableQueryFilter(
					TableQueryFilterOperands.MongoDbEqualTo,
					"client_company_id",
					clientContactRequest.clientId,
					null,
					true
				)
			],
			false
		);
		url =
			url +
			"?" +
			appliedFilters +
			`&page=${clientContactRequest.page}&limit=${clientContactRequest.limit}`;

		if (clientContactRequest.keywords) {
			url = url + `&q=${clientContactRequest.keywords}`;
		}
		if (clientContactRequest.selected_id) {
			url =
				url +
				`&selected_id=${clientContactRequest.selected_id.join(",")}`;
		}

		const response: AxiosResponse<ClientContactsResponse> = await axios(
			getRequest(url, null, true, true, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getClientDetails = async (
	clientId: string,
	includeBlocks = false
): Promise<Client> => {
	try {
		const response: AxiosResponse<Client> = await axios(
			getRequest(
				getClientDetailsUrl.replace("{client_id}", clientId) +
					"?" +
					includeChildEntities([
						ChildEntityHydrationParams.OPPORTUNITIES_ALL,
						ChildEntityHydrationParams.CLIENT_USERS_ALL,
						ChildEntityHydrationParams.DOCUMENTS_ALL,
						...(includeBlocks
							? [
									ChildEntityHydrationParams.BLOCKS_CURRENT_REVISION
							  ]
							: [])
					]),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
